import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const TEAL_GLOW = "rgba(79,205,196,0.06)";
const AMBER = "#E0A458";
const TABLE_BORDER = "rgba(255,255,255,0.08)";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 600, fontSize: "clamp(18px, 2.5vw, 22px)", color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.01em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: "0.9em", color: TEXT, background: "rgba(255,255,255,0.05)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}
function PullQuote({ children }) {
  return (
    <div style={{ borderLeft: `3px solid ${TEAL}`, paddingLeft: 20, margin: "32px 0" }}>
      <p style={{ fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.5, margin: 0 }}>{children}</p>
    </div>
  );
}
function OverviewCard({ children }) {
  return (
    <div style={{ margin: "28px 0 36px", padding: "24px 28px", borderRadius: 14, background: TEAL_GLOW, border: `1px solid ${TABLE_BORDER}` }}>
      {children}
    </div>
  );
}
function InlineCTA({ children, href, label }) {
  return (
    <div style={{ margin: "40px 0", padding: "24px 28px", borderRadius: 14, background: TEAL_DIM, border: "1px solid rgba(78,205,196,0.25)" }}>
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>{children}</p>
      <a href={href} style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}>
        {label} &#8594;
      </a>
    </div>
  );
}

const faqs = [
  {
    q: "Does blocking AI crawlers in robots.txt actually hide me from ChatGPT and Perplexity?",
    a: "Only if you block the retrieval crawlers specifically. AI engines use separate crawlers for training (GPTBot, ClaudeBot, CCBot) and for live retrieval (OAI-SearchBot, Claude-SearchBot, PerplexityBot). Blocking a training crawler opts you out of model training but does not affect citations. Blocking a retrieval crawler removes you from that engine's cited answers entirely. Most sites that want privacy mean to block training, not retrieval.",
  },
  {
    q: "Do most B2B SaaS companies block AI crawlers in their robots.txt?",
    a: "No. In a June 2026 scan of 390 B2B SaaS sites, only 0.8% blocked any AI search crawler in robots.txt and only about 1.7% blocked GPTBot. For this market, robots.txt is overwhelmingly open. If you are missing from AI answers, robots.txt is almost never the cause. The more common block happens one layer up, at the CDN or edge.",
  },
  {
    q: "Why would my site be invisible to AI search if my robots.txt allows the crawlers?",
    a: "Because robots.txt is only a request, not enforcement. A CDN or web application firewall (Cloudflare, Akamai, Fastly, DataDome) can stop an AI crawler at the edge before it ever reads your robots.txt. Cloudflare lets sites block AI crawlers by behavior, and it announced defaults for September 15, 2026 that block AI training and agent bots on pages that show ads for new sites and for existing free-plan sites that have not changed their settings. Your origin can say allow OAI-SearchBot while your edge silently blocks it, and the edge wins.",
  },
  {
    q: "How do I check if Cloudflare is blocking AI crawlers on my site?",
    a: "Open the Cloudflare dashboard, go to Security, then Analytics, and select the Events tab. Look for blocked or challenged requests from OAI-SearchBot, PerplexityBot, and Claude-SearchBot. If you find them, review Security Settings, then Configure AI bot policies, where Search, Agent, and Training bots each have their own block setting. You can also check your origin server logs: if you see Googlebot but never OAI-SearchBot or PerplexityBot, the edge is filtering them out before they reach you.",
  },
  {
    q: "What is the difference between a training crawler and a retrieval crawler?",
    a: "A training crawler (GPTBot, ClaudeBot, Google-Extended, CCBot) collects content to train foundation models, with no link or attribution back to you. A retrieval crawler (OAI-SearchBot, Claude-SearchBot, PerplexityBot) fetches pages so the AI can cite them in a live answer, which can send you traffic and visibility. The default B2B stance is allow retrieval crawlers, decide on training crawlers based on your content-licensing stance.",
  },
  {
    q: "Should I block GPTBot?",
    a: "Blocking GPTBot opts your content out of OpenAI's foundation-model training and nothing else. It does not affect your eligibility for ChatGPT Search citations, which are controlled by OAI-SearchBot. Block GPTBot if you do not want your content used to train models; allow it if you do. Either way, keep OAI-SearchBot allowed so ChatGPT can still cite you.",
  },
  {
    q: "Does an empty Disallow line block AI crawlers?",
    a: "No. An empty Disallow with no path means allow everything, which is the opposite of a block. A user-agent group with only Disallow and a blank value places no restriction on that crawler. A site is only blocking the root when it has a bare Disallow: / line. This trips up many robots.txt checkers, which is why you should verify with a tool that parses the file correctly.",
  },
];

// ── Visual 1: The scan findings panel ─────────────────────────────────
function ScanFindings() {
  const stats = [
    { big: "0.8%", label: "block an AI search crawler", sub: "in robots.txt (3 of 362 sites)" },
    { big: "1.7%", label: "block GPTBot", sub: "OpenAI's training crawler" },
    { big: "48%", label: "sit behind Cloudflare", sub: "where the edge can block AI crawlers" },
  ];
  return (
    <div style={{ margin: "28px 0 36px", border: `1px solid ${TABLE_BORDER}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", background: "rgba(255,255,255,0.03)", borderBottom: `1px solid ${TABLE_BORDER}` }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 13, fontWeight: 700, color: TEAL, letterSpacing: "0.08em", textTransform: "uppercase" }}>AI Bot Blocking Index</div>
        <div style={{ fontFamily: "var(--body)", fontSize: 13, color: TEXT_DIM, marginTop: 4 }}>390 B2B SaaS sites scanned, June 2026. 362 returned a readable robots.txt.</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        {stats.map((s, i) => (
          <div key={i} style={{ padding: "22px 20px", borderRight: i < stats.length - 1 ? `1px solid ${TABLE_BORDER}` : "none", minWidth: 0 }}>
            <div style={{ fontFamily: "var(--display)", fontSize: 34, fontWeight: 700, color: i === 2 ? AMBER : TEXT, lineHeight: 1.1 }}>{s.big}</div>
            <div style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT, fontWeight: 600, marginTop: 8 }}>{s.label}</div>
            <div style={{ fontFamily: "var(--body)", fontSize: 12.5, color: TEXT_DIM, marginTop: 4, lineHeight: 1.45 }}>{s.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Visual 2: The two-layer model ─────────────────────────────────────
function TwoLayerDiagram() {
  const layer = {
    border: `1px solid ${TABLE_BORDER}`,
    borderRadius: 12,
    padding: "18px 20px",
    background: "rgba(255,255,255,0.02)",
  };
  return (
    <div style={{ margin: "28px 0 36px", display: "grid", gap: 14 }}>
      <div style={{ ...layer, borderColor: "rgba(79,205,196,0.3)", background: TEAL_GLOW }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 13, fontWeight: 700, color: TEAL, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6 }}>Layer 1: robots.txt (a request)</div>
        <div style={{ fontFamily: "var(--body)", fontSize: 14.5, color: TEXT_DIM, lineHeight: 1.6 }}>A text file that politely asks crawlers what they may fetch. Well-behaved AI crawlers read it and obey. It does not, and cannot, force anything. For the sites we scanned, this layer is almost always wide open to AI.</div>
      </div>
      <div style={{ textAlign: "center", fontFamily: "var(--mono)", fontSize: 18, color: TEXT_DIM }}>&#8595;</div>
      <div style={{ ...layer, borderColor: "rgba(224,164,88,0.35)" }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 13, fontWeight: 700, color: AMBER, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6 }}>Layer 2: the edge / CDN (enforcement)</div>
        <div style={{ fontFamily: "var(--body)", fontSize: 14.5, color: TEXT_DIM, lineHeight: 1.6 }}>Cloudflare, Akamai, Fastly, DataDome, and other edge providers sit in front of your origin and decide which requests get through at all. This is the bouncer. Cloudflare's AI bot settings act here, before a crawler ever reaches Layer 1.</div>
      </div>
    </div>
  );
}

// ── Visual 3: The full crawler taxonomy ───────────────────────────────
function CrawlerTaxonomyTable() {
  const groups = [
    {
      type: "Retrieval / search",
      stance: "Allow: these earn your AI citations",
      tone: TEAL,
      rows: [
        ["OAI-SearchBot", "OpenAI", "Indexes pages for ChatGPT Search answers"],
        ["Claude-SearchBot", "Anthropic", "Indexes pages for Claude's web answers"],
        ["PerplexityBot", "Perplexity", "Indexes pages for Perplexity answers"],
        ["Applebot", "Apple", "Powers Siri and Apple Intelligence search"],
        ["DuckAssistBot", "DuckDuckGo", "Powers DuckAssist summaries"],
      ],
    },
    {
      type: "User-initiated",
      stance: "Allow: a real person asked the AI to fetch the page",
      tone: TEAL,
      rows: [
        ["ChatGPT-User", "OpenAI", "Fires when a ChatGPT user opens your link"],
        ["Claude-User", "Anthropic", "Fires when a Claude user opens your link"],
        ["Perplexity-User", "Perplexity", "Fires when a Perplexity user opens your link"],
      ],
    },
    {
      type: "Ads validation",
      stance: "Allow if you run (or may run) ChatGPT Ads",
      tone: TEAL,
      rows: [
        ["OAI-AdsBot", "OpenAI", "Validates ChatGPT Ads landing pages before campaigns run"],
      ],
    },
    {
      type: "Training",
      stance: "Your call: block to opt out of model training",
      tone: AMBER,
      rows: [
        ["GPTBot", "OpenAI", "Collects content to train OpenAI foundation models"],
        ["ClaudeBot", "Anthropic", "Collects content to train Anthropic models"],
        ["Google-Extended", "Google", "Opt-out token for Gemini model training"],
        ["Meta-ExternalAgent", "Meta", "Collects content to train Meta AI"],
        ["Bytespider", "ByteDance", "Collects content to train ByteDance models"],
        ["CCBot", "Common Crawl", "Open dataset used by many model trainers"],
        ["cohere-ai", "Cohere", "Collects content to train Cohere models"],
        ["Applebot-Extended", "Apple", "Opt-out token for Apple model training"],
      ],
    },
  ];
  const cellBase = { padding: "10px 14px", fontSize: 13, color: TEXT_DIM, lineHeight: 1.5, verticalAlign: "top", borderTop: `1px solid ${TABLE_BORDER}` };
  return (
    <div className="blog-scroll-wrap" style={{ margin: "28px 0 36px", overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${TABLE_BORDER}`, borderRadius: 12 }}>
      <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse", borderSpacing: 0, fontFamily: "var(--body)" }} aria-label="The major AI crawlers in 2026 grouped by type. Retrieval crawlers (OAI-SearchBot, Claude-SearchBot, PerplexityBot, Applebot, DuckAssistBot) should be allowed because they earn AI citations. User-initiated crawlers (ChatGPT-User, Claude-User, Perplexity-User) should be allowed because a person asked. OAI-AdsBot should be allowed if you run ChatGPT Ads. Training crawlers (GPTBot, ClaudeBot, Google-Extended, Meta-ExternalAgent, Bytespider, CCBot, cohere-ai, Applebot-Extended) are an optional block to opt out of model training.">
        <thead>
          <tr>
            <th style={{ ...cellBase, borderTop: "none", textAlign: "left", color: TEXT, fontWeight: 700, background: "rgba(255,255,255,0.04)", width: "26%" }}>Crawler</th>
            <th style={{ ...cellBase, borderTop: "none", textAlign: "left", color: TEXT, fontWeight: 700, background: "rgba(255,255,255,0.04)", width: "20%" }}>Operator</th>
            <th style={{ ...cellBase, borderTop: "none", textAlign: "left", color: TEXT, fontWeight: 700, background: "rgba(255,255,255,0.04)" }}>What it does</th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((g) => [
            <tr key={g.type}>
              <td colSpan={3} style={{ padding: "12px 14px", borderTop: `1px solid ${TABLE_BORDER}`, background: "rgba(255,255,255,0.02)" }}>
                <span style={{ fontFamily: "var(--display)", fontSize: 13, fontWeight: 700, color: g.tone }}>{g.type}</span>
                <span style={{ fontFamily: "var(--body)", fontSize: 12.5, color: TEXT_DIM, marginLeft: 10 }}>{g.stance}</span>
              </td>
            </tr>,
            ...g.rows.map((r, i) => (
              <tr key={g.type + i}>
                <td style={{ ...cellBase, fontFamily: "var(--mono)", fontSize: 12, color: TEXT }}>{r[0]}</td>
                <td style={cellBase}>{r[1]}</td>
                <td style={cellBase}>{r[2]}</td>
              </tr>
            )),
          ])}
        </tbody>
      </table>
    </div>
  );
}

function Content() {
  return (
    <article>
      <P>
        <Strong>Almost no B2B SaaS company blocks AI crawlers in its robots.txt.</Strong> We scanned 390 of them to be sure, and the result was lopsided: only a tiny fraction turn away the crawlers that ChatGPT, Perplexity, and Claude use to find pages. So if your brand is missing from AI answers, your robots.txt is almost certainly not the reason. The block is real, but it is usually happening one layer up, where most checkers never look.
      </P>
      <P>
        This guide is the 2026 reference for AI crawler access: every major bot and whether to allow or block it, copy-paste robots.txt templates, and the edge-layer trap (Cloudflare) that quietly overrules a perfect robots.txt. It pairs with our free <A href="/tools/ai-bot-checker">AI Bot Access Checker</A>.
      </P>

      <OverviewCard>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <Li>In our June 2026 scan, only <Strong>0.8%</Strong> of 362 readable B2B SaaS robots.txt files blocked an AI search crawler. robots.txt is rarely the problem.</Li>
          <Li>The real gatekeeper is the edge. <Strong>48%</Strong> of the sites sit behind Cloudflare, where bot settings can block AI crawlers regardless of robots.txt.</Li>
          <Li>Allow the retrieval crawlers that earn citations (OAI-SearchBot, Claude-SearchBot, PerplexityBot). Decide on training crawlers (GPTBot, ClaudeBot, CCBot) based on your content stance. Then confirm your edge agrees.</Li>
        </ul>
      </OverviewCard>

      {/* ── Section 1: the data ── */}
      <H2 id="the-scan">We scanned 390 B2B SaaS sites. Almost none block AI search.</H2>
      <P>
        To find out whether robots.txt is actually where AI visibility goes to die, we pulled the robots.txt of 390 B2B SaaS companies and classified how each one treats 17 known <A href="/glossary/ai-crawlers">AI crawlers</A> using the same parser that powers our public checker. To avoid a big-brand bias, the sample blends two cohorts: 153 recognizable established brands plus 237 active growth-stage companies from the Y Combinator directory, deduplicated. 362 returned a readable file.
      </P>
      <ScanFindings />
      <P>
        The pattern is clear. Blocking is rare, and where it happens it is deliberate and narrow. Only <Strong>six sites</Strong> blocked GPTBot (figma.com, mutiny.com, loom.com, sendbird.com, usenash.com, volopay.com), and most still allow the retrieval crawlers, the textbook "do not train on me, but do cite me" stance (loom blocks GPTBot and nothing else). Figma is the single site in the sample that blocks OpenAI's retrieval crawler outright. <Strong>One site</Strong> in 362 blocks ChatGPT Search. That is the headline: for this market, robots.txt is not a wall, it is an open door.
      </P>
      <PullQuote>
        Only one of 362 B2B SaaS sites blocks ChatGPT Search in robots.txt. If you are missing from AI answers, the file you keep editing is almost never the cause.
      </PullQuote>
      <P>
        Note the honest limits. The sample blends a curated set of well-known brands with a reproducible pull from the Y Combinator directory, so it is not a random sample of the entire market, and "served via Cloudflare" is measured from response headers, not a per-site test of whether each one is actively turning bots away. But the direction is unambiguous across both cohorts, and it reframes the entire question. The interesting story is not the 0.8% who block in robots.txt. It is the 48% sitting behind an edge that can block for them, silently.
      </P>
      <P>
        The full per-site results for this June scan, the per-crawler block rates, and downloadable JSON and CSV live in the <A href="/research/ai-bot-blocking-index/2026-q2">June 2026 edition</A> of the AI Bot Blocking Index. We re-scan the same 390 sites every quarter: the <A href="/research/ai-bot-blocking-index/2026-q3">September 15, 2026 edition</A> found 0.6% blocking an AI search crawler, and our <A href="/blog/are-b2b-saas-companies-blocking-ai-crawlers">September analysis</A> covers what changed site by site. The <A href="/research/ai-bot-blocking-index">main study page</A> always carries the newest edition.
      </P>

      {/* ── Section 2: two-layer model ── */}
      <H2 id="two-layer">Why your robots.txt is not the problem: the two-layer model</H2>
      <P>
        AI crawler access is decided at two layers, and they are easy to confuse because only one of them is visible to you as a text file you can edit.
      </P>
      <TwoLayerDiagram />
      <P>
        Robots.txt is a request. It is the polite sign on the door that says "staff only past this point." Honest crawlers honor it. But it has no power to stop anything, and it is the layer almost everyone fixates on because it is the one you can see and change. The edge is enforcement. It is the bouncer who decides whether the visitor gets in the building at all, and it runs before your robots.txt is ever read. When the two disagree, the edge wins every time.
      </P>
      <P>
        This is why a site can have a flawless robots.txt and still earn zero <A href="/glossary/ai-citation">AI citations</A>. The origin says "allow OAI-SearchBot." The edge says "block unknown bots." OpenAI's crawler is turned away at the door, your page is never fetched, and nothing in your robots.txt can override it. The fix is never in the file you keep editing. It is in the layer above it.
      </P>

      {/* ── Section 3: Cloudflare ── */}
      <H2 id="cloudflare-default">The real gatekeeper: Cloudflare&apos;s AI crawler settings</H2>
      <P>
        On July 1, 2025, in an announcement it called <A href="https://blog.cloudflare.com/content-independence-day-no-ai-crawl-without-compensation/">Content Independence Day</A>, Cloudflare changed the default for new domains to block AI crawlers that access content without permission or compensation. The intent was to give publishers leverage over AI companies. On July 1, 2026, Cloudflare <A href="https://www.cloudflare.com/press/press-releases/2026/cloudflare-allows-the-agentic-internet-to-flourish-with-a-simple-philosophy-your-content-your-rules/">announced new defaults</A> for September 15, 2026: new sites, and existing free-plan sites that have not changed their settings, allow search crawlers but block AI training and agent bots on pages that show ads. Agent bots include chat fetch bots such as ChatGPT-User, which act in real time on a person&apos;s behalf. And <A href="https://developers.cloudflare.com/bots/additional-configurations/block-ai-bots/">any setting that blocks AI training</A>, including the older Block AI bots option, also blocks crawlers that combine search and training, such as Googlebot, Applebot, and Bingbot. The side effect, for a B2B marketer trying to get cited, is that a Cloudflare setting can cut off AI crawlers without anyone touching robots.txt.
      </P>
      <P>
        In our scan, <Strong>48% of sites</Strong> are served through Cloudflare. We did not test each one's bot settings, so we cannot say how many actively block, but every one of them is a site where the AI-crawler decision is being made at the edge, not in the file their marketing team can see. And the effect is not theoretical: five sites in our sample (gusto.com, otterly.ai, and rootly.com among them) returned an outright block to our own checker's request, a small live demonstration of an edge turning an unfamiliar visitor away before robots.txt is ever consulted.
      </P>
      <PullQuote>
        Your robots.txt says allow OAI-SearchBot. Your Cloudflare settings say block it. Cloudflare wins, OpenAI never reaches you, and your AI visibility quietly dies.
      </PullQuote>
      <P>
        The same dynamic exists at Akamai, Fastly, Imperva, and DataDome. Each treats unfamiliar automated traffic as suspicious by default. None of them reads your robots.txt before deciding. So the first question for any site missing from AI answers is not "what does my robots.txt say," it is "what does my edge do with an AI crawler it does not recognize." We will check that directly in a moment.
      </P>

      {/* ── Section 4: taxonomy ── */}
      <H2 id="crawler-reference">Every AI crawler in 2026: which to allow, which to block</H2>
      <P>
        There are three kinds of AI crawler, and the right default is different for each. Retrieval crawlers fetch pages so an AI can cite them in a live answer, which is how you earn visibility and traffic, so you allow them. User-initiated crawlers fire only when a real person asks an AI to open your link, so you allow them too. Training crawlers collect content to train models with no attribution back, so blocking them is a content-licensing decision, not a visibility one.
      </P>
      <CrawlerTaxonomyTable />
      <P>
        The single most expensive mistake is blocking a retrieval crawler while trying to block training. OAI-SearchBot sounds like it might gather training data; it does not. It is the crawler that lets ChatGPT cite you. The training crawler you actually meant to block is GPTBot. The two are independent: blocking GPTBot opts you out of OpenAI training and leaves your ChatGPT citations untouched, while blocking OAI-SearchBot removes you from ChatGPT answers and does nothing about training. For the deeper version of that specific decision, see <A href="/blog/gptbot-vs-oai-searchbot">our GPTBot vs OAI-SearchBot breakdown</A>.
      </P>

      {/* ── Section 5: templates ── */}
      <H2 id="robots-txt-templates">Copy-paste robots.txt templates for 3 scenarios</H2>
      <P>
        Pick the block that matches your stance, paste it at your site root, and confirm it serves at <Code>https://yourdomain.com/robots.txt</Code> with an HTTP 200. Group each crawler under its own user-agent line rather than relying on a wildcard, since the operators control each bot independently.
      </P>

      <H3>Scenario 1: Maximum AI visibility (recommended for most B2B)</H3>
      <P>
        Allow every retrieval and user-initiated crawler. This is the right default for most SaaS, services, and content sites that want to be cited everywhere AI answers appear.
      </P>
      <CodeBlock lang="robots.txt (maximum AI visibility)" syntax="text">{`# Allow AI retrieval + user-initiated crawlers (earn citations)
User-agent: OAI-SearchBot
User-agent: ChatGPT-User
User-agent: Claude-SearchBot
User-agent: Claude-User
User-agent: PerplexityBot
User-agent: Perplexity-User
User-agent: Applebot
User-agent: DuckAssistBot
Allow: /

# Everyone else: normal crawl
User-agent: *
Allow: /`}</CodeBlock>

      <H3>Scenario 2: Cited but not trained on (privacy-conscious B2B)</H3>
      <P>
        Allow retrieval, block training. You stay eligible for AI citations while opting out of the model-training pipelines that give nothing back.
      </P>
      <CodeBlock lang="robots.txt (allow retrieval, block training)" syntax="text">{`# Allow retrieval crawlers (for AI citations)
User-agent: OAI-SearchBot
User-agent: Claude-SearchBot
User-agent: PerplexityBot
User-agent: Applebot
Allow: /

# Block training crawlers (opt out of model training)
User-agent: GPTBot
User-agent: ClaudeBot
User-agent: Google-Extended
User-agent: CCBot
User-agent: Bytespider
User-agent: Meta-ExternalAgent
User-agent: Applebot-Extended
User-agent: cohere-ai
Disallow: /`}</CodeBlock>

      <H3>Scenario 3: Block all AI (paywalled, regulated, licensed content)</H3>
      <P>
        Block training and retrieval. You exit AI answers entirely. Fit for paywalled publishers and regulated industries where every form of AI access is restricted.
      </P>
      <CodeBlock lang="robots.txt (block all AI)" syntax="text">{`# Block all major AI crawlers, training and retrieval
User-agent: GPTBot
User-agent: OAI-SearchBot
User-agent: ClaudeBot
User-agent: Claude-SearchBot
User-agent: PerplexityBot
User-agent: Google-Extended
User-agent: CCBot
User-agent: Bytespider
User-agent: Meta-ExternalAgent
User-agent: Applebot-Extended
User-agent: cohere-ai
Disallow: /`}</CodeBlock>

      <P>
        One trap to avoid: an empty <Code>Disallow:</Code> line with no path means allow everything, not block everything. A crawler is only blocked from your whole site when its group contains a bare <Code>Disallow: /</Code>. Mixing these up is the most common robots.txt error, and it is why a checker that parses the file correctly matters more than eyeballing it.
      </P>

      {/* ── Section 6: fix Cloudflare ── */}
      <H2 id="fix-cloudflare">How to check and fix Cloudflare's AI-crawler block</H2>
      <P>
        Since the edge can overrule robots.txt, this is the highest-leverage thing to check. In Cloudflare, open the dashboard, go to <Strong>Security, then Analytics</Strong>, and select the <Strong>Events</Strong> tab. Look for blocked or challenged requests from <Code>OAI-SearchBot</Code>, <Code>PerplexityBot</Code>, and <Code>Claude-SearchBot</Code>. If you find them, a Cloudflare setting or rule is turning away the crawlers that earn citations.
      </P>
      <P>
        The main control is under <Strong>Security Settings, then Configure AI bot policies</Strong>. Search, Agent, and Training bots each get their own setting: block on all pages, block only on pages that show ads, or allow. If you want AI citations, keep Search allowed, and remember that blocking Training also blocks crawlers that combine search and training, such as Googlebot. The older <Strong>Block AI bots</Strong> setting is <A href="https://developers.cloudflare.com/bots/additional-configurations/block-ai-bots/">being retired on September 15, 2026</A>. If you run Super Bot Fight Mode, a WAF custom rule can let the retrieval crawlers you want skip it:
      </P>
      <CodeBlock lang="Cloudflare WAF custom rule (expression)" syntax="text">{`# Let AI retrieval crawlers skip Super Bot Fight Mode
(http.user_agent contains "OAI-SearchBot") or
(http.user_agent contains "Claude-SearchBot") or
(http.user_agent contains "PerplexityBot") or
(http.user_agent contains "Applebot")

Action: Skip
Skip:
  - Super Bot Fight Mode
  - All remaining custom rules`}</CodeBlock>
      <P>
        Bot Fight Mode, the free version, cannot be skipped this way. <A href="https://developers.cloudflare.com/bots/get-started/bot-fight-mode/">Cloudflare says</A> Skip, Bypass, and Allow actions have no effect on it, so if Bot Fight Mode is challenging a crawler you need, switch it off.
      </P>
      <P>
        The principle generalizes to every edge provider: your CDN and firewall rules must mirror your robots.txt intent, or robots.txt is silently overruled. Allowlist the same retrieval crawlers at Akamai, Fastly, Imperva, or DataDome if you run them. Verify legitimacy by reverse-resolving a sample of crawler IPs against the operator's published ranges, so a spoofed user-agent does not earn a free pass.
      </P>

      <InlineCTA href="/tools/ai-bot-checker" label="Run the free AI Bot Access Checker">
        Not sure whether AI crawlers can actually reach your site? The free AI Bot Access Checker tests your live robots.txt against 25 AI crawlers and flags Cloudflare at the edge. Takes 60 seconds, no signup.
      </InlineCTA>

      {/* ── Section 7: verify ── */}
      <H2 id="verify">How to confirm AI crawlers can actually reach you</H2>
      <P>
        Editing robots.txt and edge rules is only half the job. The other half is confirming the change worked from the outside, the way a crawler sees it. Three checks, in order of effort:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Run the checker.</Strong> Our <A href="/tools/ai-bot-checker">AI Bot Access Checker</A> tests your live URL against all 25 crawlers and detects whether you are served through Cloudflare, so you see both layers at once.</Li>
        <Li><Strong>Read your server logs.</Strong> Look for retrieval user-agents (<Code>OAI-SearchBot</Code>, <Code>PerplexityBot</Code>, <Code>Claude-SearchBot</Code>). If you see Googlebot but never these, the edge is filtering them before they reach your origin. Tracking this over time is covered in <A href="/blog/how-to-track-ai-bot-activity">our bot activity guide</A>.</Li>
        <Li><Strong>Watch your citations.</Strong> Reaching the page is necessary but not sufficient; the page still has to be worth citing. Once crawlers can get in, the work shifts to <A href="/glossary/answer-engine-optimization">answer engine optimization</A>: structured, quotable content that AI engines choose to surface.</Li>
      </ul>
      <P>
        Crawler access is the floor, not the ceiling. It gets you eligible. Earning the citation is a separate discipline, and the two together are what move <A href="/glossary/ai-visibility">AI visibility</A>. But you cannot optimize your way into an answer if the bot never reaches the page, which is exactly why the edge check has to come first.
      </P>

      {/* ── FAQPage JSON-LD ── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* ── FAQ ── */}
      <H2 id="faq">Frequently asked questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related Reading ── */}
      <H2 id="related">Related reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/gptbot-vs-oai-searchbot">GPTBot vs OAI-SearchBot: Why Blocking the Wrong One Kills Your ChatGPT Visibility</A></Li>
        <Li><A href="/blog/what-is-oai-adsbot">What Is OAI-AdsBot? (OpenAI's ChatGPT Ads Crawler)</A></Li>
        <Li><A href="/blog/how-to-track-ai-bot-activity">How to Track AI Bot Activity on Your Website</A></Li>
        <Li><A href="/blog/what-is-llms-txt">What Is llms.txt and Does Your Website Need One?</A></Li>
        <Li><A href="/blog/how-to-optimize-website-for-ai-search">How to Optimize a Website for AI Search (2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "robots-txt-ai-bots-2026",
  title: "AI Crawlers in 2026: Which Bots to Allow and Which to Block",
  seoTitle: "AI Crawlers in 2026: Which to Allow, Which to Block",
  excerpt: "We scanned 390 B2B SaaS sites in June 2026: only 0.8% blocked an AI search crawler in robots.txt. The real gatekeeper is the edge, where 48% sit behind Cloudflare. The full crawler reference, copy-paste robots.txt templates, and how to fix a Cloudflare block.",
  metaDescription: "We scanned 390 B2B SaaS sites: almost none block AI crawlers in robots.txt, but Cloudflare's edge can still block them. Which AI bots to allow, which to block.",
  date: "2026-06-26",
  lastUpdated: "2026-09-15",
  readTime: "10 min read",
  category: "AI Analytics",
  fiveA: "analytics",
  tags: ["AI crawlers", "robots.txt", "Cloudflare", "GPTBot", "OAI-SearchBot", "AI Analytics"],
  Content,
};

export default post;
